import React, { useEffect, useRef } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'react-bootstrap/Button'
import { useReactToPrint } from 'react-to-print'
import { generateBarcode, getSampleDetails, shippedSample } from '../../services/Sample'
import { useParams } from 'react-router-dom'
import { AppUtil } from '../../utils/app.utils'
import { Col, Row, Stack } from 'react-bootstrap'
import { Sample } from '../../models/Sample'
import { ViewSampleDetails } from '../ViewPatient/components/ViewSampleDetails'
import { trackPromise } from 'react-promise-tracker'
import '../ViewPatient/components/styles/ViewSamples.scss'
import { SampleStatusMasterData } from '../SampleInformationForm/helpers'
import { PRIVILEGES } from '../../utils/constants'
import { AuthUtil } from '../../utils/auth.utils'
import QRCode from 'react-qr-code'
import './SampleBarcodeGenerate.scss'
import { printQR } from '../../utils/printQR.utils'
import { ConfirmationPopUp } from '../CommonComponents'

interface BarcodeInfoProps {
  show: boolean,
  label1: string[],
  label2: string[],
  barcode: string
}

const shippedOrReportReady = [
  SampleStatusMasterData.sampleStatusValueMap.shipped,
  SampleStatusMasterData.sampleStatusValueMap.report_ready,
];

const readyToShip = [
  SampleStatusMasterData.sampleStatusValueMap.ready_for_shipment
]

export const BarcodeGenerate = (props: any) => {
  const { buttonClass, setValue, getValues } = props
 
  const [barcodeInfo, setBarcodeInfo] = useState<BarcodeInfoProps | null>(null)
  const { id, patientId } = useParams()
  const contentToPrint = useRef(null)
  const tanslateDM = 'sampleBarcode'
  const viewSampleDetialsParentClass = 'view-samples'
  const parentClass = 'vcr-pp-sample-info-'
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState<boolean>(false);
  const [sampleLists, setSampleLists] = useState<Sample[]>([])

  useEffect(() => {
    if (!getValues('sampleStatus')) {
      setBarcodeInfo({ show: false, label1: [], label2: [], barcode: '' })
    }
  }, [getValues('sampleStatus')])

  const isReadyToShip =
    readyToShip.includes(getValues('sampleStatus'))

  const [isShippedOrReportReady, setIsShippedOrReportReady] = useState<boolean>(
    shippedOrReportReady.includes(getValues('sampleStatus')))

  const hasEditAfterShippmentPrivilege = AuthUtil.checkUserAccess(PRIVILEGES.EDIT_AFTER_SHIPMENT)

  const fetchSampleLists = async () => {
    try {
      const sampleListsData = await trackPromise(getSampleDetails(id ?? '', patientId ?? ''))
      if (sampleListsData.status === 200) {
        setValue('sampleStatus', sampleListsData.data.sampleStatus)        
        setSampleLists([{ ...sampleListsData.data }])
      }
    } catch (error: any) {
      AppUtil.logError(error)
    }
  }

  const handleShow = async () => {
    try {
      const res = await generateBarcode(id ?? '')
      const { deidentifiedData, testDetails, sampleStatus, blockLable } = res.data
      setIsShippedOrReportReady(
        shippedOrReportReady.includes(sampleStatus),
      )
      if (!shippedOrReportReady.includes(sampleStatus)) {
        setValue('sampleStatus', sampleStatus)
      }
      setBarcodeInfo({ show: true, label1: testDetails, label2: blockLable, barcode: deidentifiedData })
      //setValue('barcode', deidentifiedData)
    } catch (error: any) {
      AppUtil.logError(error)
    }
  }

  const handleSuccess = () => {
    setShowModal(false)
    handleShow()
    
  }

  useEffect(() => {
    if (isReadyToShip || isShippedOrReportReady) {
      handleShow()
      if (isShippedOrReportReady) {
        fetchSampleLists()
      }
    }
  }, [])

  async function printQRCode() {
    const res = await generateBarcode(id ?? "");
    printQR(res.data.deidentifiedData, res.data.blockLable, res.data.testDetails);
  }

  const handleShipped = async () => {
    try {
      const isShipped = await trackPromise(shippedSample(id ?? ''))
      if (isShipped.status === 200) {
        setValue('sampleStatus', isShipped.data.sampleStatus)
        setIsShippedOrReportReady(
          shippedOrReportReady.includes(isShipped.data.sampleStatus),
        )
        fetchSampleLists()
      }
    } catch (error: any) {
      AppUtil.logError(error)
    }
  }

  return (
    <>
      <ConfirmationPopUp title={t(`${tanslateDM}.modal.title`)} description={t(`${tanslateDM}.modal.description`)} show={showModal} handleModalClose={() => setShowModal(false)} saveBtnText={t(`${tanslateDM}.modal.saveBtnText`)} handleSuccess={handleSuccess} note={t(`${tanslateDM}.modal.note`)}/>
      {!barcodeInfo?.show && (
        <Button className={buttonClass} variant="primary" onClick={() => setShowModal(true)} >
          {t(`${tanslateDM}.createBarcode`)}
        </Button>
      )}

      {barcodeInfo?.show && (
        <Stack gap={3}>
          <div ref={contentToPrint}>
            <Row>
              <Col lg={3}>
                <h5 className={'mb-4'}>{t(`${tanslateDM}.clinicalSampleBarcode`)}</h5>
                <div className={`${parentClass}qrInfo`}>
                  <QRCode
                    className={`${parentClass}qrcode`}
                    value={barcodeInfo?.barcode}
                    viewBox={`0 0 25 25`}
                  />
                  <div className={`${parentClass}qrcode-label`}>{barcodeInfo?.label1}</div>
                </div>
              </Col>
            </Row>
            <div>
            <Row>
              {barcodeInfo?.label2?.length > 0 && (<h5>{t(`${tanslateDM}.biorepositorySamplesBarcode`)}</h5>)
              }
              {barcodeInfo?.label2?.map((info, index) => (
                <Col lg={3} key={index} className={`${parentClass}qrInfo`}>                
                  <QRCode
                    className={`${parentClass}qrcode`}
                    value={barcodeInfo.barcode}
                    viewBox={`0 0 25 25`}
                  />
                  <div className={`${parentClass}qrcode-label`}>{info}</div>
                </Col>
              ))
              }
            </Row>
            </div>
          </div>
          <Row>
            <Col>
              <Button className={`${parentClass}save-create-barcode mx-0`} onClick={() => printQRCode()}>
                {t(`${tanslateDM}.printBarcode`)}
              </Button>

              {!isShippedOrReportReady && !hasEditAfterShippmentPrivilege && (
                <Button className={`${parentClass}save-create-barcode`} onClick={() => handleShipped()}>
                  {t(`${tanslateDM}.shipSample`)}
                </Button>
              )}
            </Col>
          </Row>
          <br />
          {sampleLists.map((sample, index) => (
            <div key={index}>
              <div className={`${viewSampleDetialsParentClass}`}>
                <h5>{t(`${tanslateDM}.shippedBarcodeDetails`)}</h5>
                <ViewSampleDetails patientID={patientId ?? ''} sample={sample} index={index} />
              </div>
            </div>
          ))}
        </Stack>
      )}
    </>
  )
}
